import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cloudinaryUrl } from '@/lib/cloudinary';
import { cn } from '@/lib/cn';
import type { ProductImage } from '@/types/product';

interface ProductImageGalleryProps {
  images: ProductImage[];
  productName: string;
  className?: string;
  /** When provided, renders a sibling overlay (e.g. FabricRipple). */
  overlay?: React.ReactNode;
  /** Notifies parent when active image changes (used by FabricRipple). */
  onActiveImageChange?: (index: number, container: HTMLDivElement | null) => void;
  /** Selected colour's photo — when it matches a gallery image, jump to it. */
  activeImageUrl?: string | null;
}

const SWIPE_THRESHOLD = 60;

export function ProductImageGallery({
  images,
  productName,
  className,
  overlay,
  onActiveImageChange,
  activeImageUrl,
}: ProductImageGalleryProps) {
  const [active, setActive] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);

  const sorted = [...images].sort((a, b) => a.sortOrder - b.sortOrder);
  const current = sorted[active];

  useEffect(() => {
    onActiveImageChange?.(active, containerRef.current);
  }, [active, onActiveImageChange]);

  // When the shopper picks a colour, jump the gallery to that colour's photo
  // (if it's one of the gallery images).
  useEffect(() => {
    if (!activeImageUrl) return;
    const idx = images
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .findIndex((im) => (im.url ?? im.cloudinaryId) === activeImageUrl);
    if (idx >= 0) setActive(idx);
  }, [activeImageUrl, images]);

  const goTo = (idx: number) => {
    if (sorted.length === 0) return;
    const wrapped = (idx + sorted.length) % sorted.length;
    setActive(wrapped);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    pointerStart.current = { x: e.clientX, y: e.clientY };
  };

  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      goTo(dx > 0 ? active - 1 : active + 1);
    }
  };

  if (sorted.length === 0) {
    return (
      <div className={cn('relative aspect-[3/4] rounded-lg bg-[color:var(--color-bg-surface)]', className)} />
    );
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div
        ref={containerRef}
        className="relative aspect-[3/4] cursor-zoom-in overflow-hidden rounded-lg bg-[color:var(--color-bg-surface)]"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onClick={() => setZoomed((v) => !v)}
      >
        <AnimatePresence mode="wait">
          {current && (
            <motion.img
              key={current.id}
              src={cloudinaryUrl(current.url ?? current.cloudinaryId, { width: 1200, crop: 'fill' }) ?? ''}
              alt={current.altText ?? productName}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, scale: zoomed ? 1.6 : 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
              className="absolute inset-0 h-full w-full object-cover"
              draggable={false}
            />
          )}
        </AnimatePresence>

        {overlay && <div className="pointer-events-none absolute inset-0">{overlay}</div>}

        {sorted.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous image"
              onClick={(e) => {
                e.stopPropagation();
                goTo(active - 1);
              }}
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-[color:var(--color-bg-deep)]/60 p-2 text-white backdrop-blur-sm transition-opacity hover:opacity-90"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Next image"
              onClick={(e) => {
                e.stopPropagation();
                goTo(active + 1);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-[color:var(--color-bg-deep)]/60 p-2 text-white backdrop-blur-sm transition-opacity hover:opacity-90"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </>
        )}
      </div>

      {sorted.length > 1 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {sorted.map((img, i) => (
            <button
              key={img.id}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Show image ${i + 1}`}
              className={cn(
                'relative h-[96px] w-[72px] shrink-0 overflow-hidden rounded-md border transition-colors',
                i === active
                  ? 'border-[color:var(--color-brand-magenta)]'
                  : 'border-white/10 hover:border-white/30'
              )}
            >
              <img
                src={cloudinaryUrl(img.url ?? img.cloudinaryId, { width: 144, crop: 'fill' }) ?? ''}
                alt={img.altText ?? `${productName} thumbnail ${i + 1}`}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
